import React from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const PackageCard = ({ tripPackage, onInstantBook, onBook }) => {
    const auth = useAuth();
    const navigate = useNavigate();
    return (
        <Row className="px-2 mt-2">
            <Col xs={12} className="px-3">
                <Row className="cs-bg-dark2 p-3">
                    <Col xs={12} className="d-flex flex-wrap align-items-center justify-content-between mb-2">
                        <h6 className="mb-0">{tripPackage.name}</h6>
                        <div className="d-flex flex-wrap flex-column gap-2">
                            {tripPackage.allowBooking && !tripPackage.instantBook && typeof onBook === 'function' && (
                                <Button
                                    variant="outline-light"
                                    size="sm"
                                    className="d-flex align-items-center justify-content-center gap-1"
                                    style={{ borderColor: '#ddb467', color: '#ddb467', fontFamily: '"Fjalla One", sans-serif' }}
                                    onClick={() => {
                                        if (auth.currentUser) {
                                            if (typeof onBook === 'function') onBook(tripPackage);
                                        } else {
                                            navigate(`/auth?redirect_to=${encodeURIComponent(window.location.pathname)}`);
                                        }
                                    }}
                                >
                                    <i className="material-icons" style={{ fontSize: '1rem' }}>book_online</i>
                                    Book
                                </Button>
                            )}
                            {tripPackage.instantBook && (
                                <Button
                                    variant="outline-light"
                                    size="sm"
                                    className="d-flex align-items-center justify-content-center gap-1"
                                    style={{ borderColor: '#ddb467', color: '#ddb467', fontFamily: '"Fjalla One", sans-serif' }}
                                    onClick={() => {
                                        if (auth.currentUser) {
                                            onInstantBook(tripPackage);
                                        } else {
                                            // Redirect to login page with return URL
                                            navigate(`/auth?redirect_to=${encodeURIComponent(window.location.pathname)}`);
                                        }
                                    }}
                                >
                                    <i className="material-icons" style={{ fontSize: '1rem' }}>flash_on</i>
                                    Instant Book
                                </Button>
                            )}
                            {tripPackage.freeCancellation && (
                                <div className="d-flex align-items-center text-info">
                                    <i className="material-icons me-1" style={{ fontSize: '1rem' }}>check_circle</i>
                                    <span style={{ fontSize: '0.85rem', fontWeight: 500, fontFamily: '"Fjalla One", sans-serif' }}>Free Cancellation</span>
                                </div>
                            )}
                        </div>
                    </Col>
                    <Col xs={6} md={3}>
                        <p className="mb-0 fs-10">Duration</p>
                        <p className="mb-0">
                            {/^\d+$/.test(tripPackage.duration) ? `${tripPackage.duration} Hours` : tripPackage.duration}
                        </p>
                    </Col>
                    <Col xs={6} md={3}>
                        <p className="mb-0 fs-10">Base Price</p>
                        <p className="mb-0">${tripPackage.basePrice}</p>
                        {parseInt(tripPackage.priceChangeOptions?.afterGuests) > 0 && (
                            <p className="mb-0 text-white-50" style={{ fontSize: '0.75rem' }}>Up to {tripPackage.priceChangeOptions.afterGuests} additional passengers</p>
                        )}
                    </Col>
                    <Col xs={6} md={2}>
                        <p className="mb-0 fs-10">Max Passengers</p>
                        <p className="mb-0">
                            {(() => {
                                const base = parseInt(tripPackage.maxGuests) || 0;
                                const extra = parseInt(tripPackage.priceChangeOptions?.afterGuests) || 0;
                                return extra > 0 ? base + extra : base;
                            })()}
                        </p>
                    </Col>
                    {tripPackage.priceChangeOptions?.extraPersonPrice && Number(tripPackage.priceChangeOptions?.afterGuests) > 0 && (
                        <Col xs={6} md={2}>
                            <p className="mb-0 fs-10">Extra Passenger</p>
                            <p className="mb-0">
                                +${tripPackage.priceChangeOptions?.extraPersonPriceType === 'flat' 
                                    ? parseInt(tripPackage.priceChangeOptions.extraPersonPrice) || 0
                                    : Math.round(((parseInt(tripPackage.basePrice) || 0) * (parseInt(tripPackage.priceChangeOptions.extraPersonPrice) || 0)) / 100)}
                            </p>
                        </Col>
                    )}
                    <Col xs={6} md={2}>
                        <p className="mb-0 fs-10">Availability</p>
                        <p className="mb-0">
                            {tripPackage.availableDateRange?.startDate && tripPackage.availableDateRange?.endDate
                                ? `${tripPackage.availableDateRange.startDate} - ${tripPackage.availableDateRange.endDate}`
                                : tripPackage.seasonalAvailability || 'Year-round'}
                        </p>
                    </Col>
                    {tripPackage.startTimeOptions && tripPackage.startTimeOptions.length > 0 && (
                        <Col xs={6} md={3} className="mt-2">
                            <p className="mb-0 fs-10">Start Times</p>
                            <p className="mb-0">{tripPackage.startTimeOptions.join(', ')}</p>
                        </Col>
                    )}
                    {tripPackage.daysOffered && (
                        <Col xs={12} md={4} className="mt-2 ">
                            <p className="mb-1 fs-10">Days Offered</p>
                            <div className="d-flex gap-1 flex-wrap justify-content-md-center">
                                {[
                                    { key: 'Fri', label: 'Fri' },
                                    { key: 'Sat', label: 'Sat' },
                                    { key: 'Sun', label: 'Sun' },
                                    { key: 'Mon', label: 'Mon' },
                                    { key: 'Tue', label: 'Tue' },
                                    { key: 'Wed', label: 'Wed' },
                                    { key: 'Thu', label: 'Thu' }
                                ].map((day) => {
                                    const isOffered = tripPackage.daysOffered[day.key];
                                    return (
                                        <div
                                            key={day.key}
                                            className="d-flex align-items-center justify-content-center border"
                                            style={{
                                                width: '32px',
                                                height: '24px',
                                                borderRadius: '4px',
                                                fontSize: '0.65rem',
                                                fontWeight: 'bold',
                                                backgroundColor: isOffered ? '#fff' : 'transparent',
                                                borderColor: isOffered ? '#fff' : 'rgba(255,255,255,0.2)',
                                                color: isOffered ? '#000' : 'rgba(255,255,255,0.4)',
                                                fontFamily: '"Fjalla One", sans-serif'
                                            }}
                                            title={day.key}
                                        >
                                            {day.label}
                                        </div>
                                    );
                                })}
                            </div>
                        </Col>
                    )}
                    {tripPackage.description && (
                        <Col xs={12} className="mt-2">
                            <p className="mb-0 fs-10">Description</p>
                            <p className="mb-0">{tripPackage.description}</p>
                        </Col>
                    )}
                </Row>
            </Col >
        </Row >
    );
};

export default PackageCard;
